import React, { useEffect, useRef, useState } from "react";
import api from "./api";
import {
  HiOutlineUser,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineExclamationCircle
} from "react-icons/hi2";

import Dashboard from "./components/Dashboard";
import ProductionEntry from "./components/ProductionEntry";
import Reports from "./components/Reports";
import AdminPanel from "./components/AdminPanel";
import Profile from "./components/Profile";
import ResetPassword from "./components/ResetPassword";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Requests from "./components/Requests";
import ManagerDashboard from "./components/ManagerDashboard";
import ManagerReports from "./components/ManagerReports";
import EmployeeDetails from "./components/EmployeeDetails";
import Inbox from "./components/Inbox";
import ManagerInbox from "./components/ManagerInbox";
import ManagerRequests from "./components/ManagerRequests";
import AdminInbox from "./components/AdminInbox";
import SuperAdminPanel from "./components/SuperAdminPanel";
import LandingPage from "./components/LandingPage";

const HEADER_HEIGHT = 68;
const INACTIVITY_LIMIT = 30 * 60 * 1000;

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F0FDFA",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#D1D5DB",
  borderFocus: "#14B8A6",
  danger: "#DC2626",
  dangerBg: "#FEF2F2",
  dangerBorder: "#FECACA"
};

function getHomePageForRole(role) {
  if (role === "superadmin") return "superadmin";
  if (role === "manager") return "manager";
  if (role === "admin") return "admin";
  return "dashboard";
}

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("landing");

  const PAGE_TO_URL = {
    "dashboard": "/dashboard", "production": "/production",
    "reports": "/reports", "profile": "/profile",
    "reset": "/reset-password", "superadmin": "/superadmin",
    "admin": "/admin", "requests": "/requests", "inbox": "/inbox",
    "managerInbox": "/manager-inbox", "managerRequests": "/manager-requests",
    "adminInbox": "/admin-inbox", "manager": "/manager",
    "managerReports": "/manager-reports", "employeeDetails": "/employee-details",
    "landing": "/"
  };

  const navigate = (pageName, customUrl) => {
    setPage(pageName);
    const url = customUrl || PAGE_TO_URL[pageName] || "/" + pageName;
    if (window.location.pathname !== url) {
      window.history.pushState({ page: pageName }, "", url);
    }
  };
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminTab, setAdminTab] = useState("dashboard");
  const [superAdminTab, setSuperAdminTab] = useState("dashboard");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loginBtnHover, setLoginBtnHover] = useState(false);
  const [eyeHover, setEyeHover] = useState(false);

  const inactivityTimerRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setSidebarOpen(false);
    setSelectedEmployee(null);
    setAdminTab("dashboard");
    setPage("dashboard");
    window.history.replaceState({}, "", "/");
    setUser(null);
    setName("");
    setPassword("");
    setShowPassword(false);
    setLoginError("");

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const resetInactivityTimer = () => {
    if (!user) return;

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      alert("You have been logged out due to inactivity.");
      handleLogout();
    }, INACTIVITY_LIMIT);
  };

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("token");

    if (!savedUser || !savedToken) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      return;
    }

    try {
      const parsedUser = JSON.parse(savedUser);

      if (parsedUser?.id) {
        setUser(parsedUser);
        const homePg = getHomePageForRole(parsedUser.role);
        const U2P = {
          "/dashboard":"dashboard", "/production":"production", "/reports":"reports",
          "/profile":"profile", "/reset-password":"reset", "/superadmin":"superadmin",
          "/create-users":"superadmin", "/revenue":"superadmin",
          "/admin":"admin", "/manageusers":"admin", "/controls":"admin", "/hierarchy":"admin",
          "/requests":"requests", "/inbox":"inbox",
          "/manager-inbox":"managerInbox", "/manager-requests":"managerRequests",
          "/admin-inbox":"adminInbox", "/manager":"manager",
          "/manager-reports":"managerReports", "/employee-details":"employeeDetails"
        };
        const urlPage = U2P[window.location.pathname];
        setPage(urlPage || homePg);
      } else {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    } catch (err) {
      console.error("Failed to restore user from storage:", err);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  }, []);

  useEffect(() => {
    if (!user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click"
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    resetInactivityTimer();

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user]);

  const login = async () => {
    setLoginError("");

    if (!name.trim() || !password.trim()) {
      setLoginError("Please enter username and password.");
      return;
    }

    try {
      setLoggingIn(true);

      const res = await api.post("/login", {
        name: name.trim(),
        password
      });

      const loggedInUser = res.data?.user;
      const token = res.data?.token;

      if (!loggedInUser || !token) {
        throw new Error("Invalid login response");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(loggedInUser));

      setUser(loggedInUser);
      setSidebarOpen(false);
      setSelectedEmployee(null);
      setAdminTab("dashboard");
      setName("");
      setPassword("");
      setShowPassword(false);

      setPage(getHomePageForRole(loggedInUser.role));
    } catch (err) {
      console.error(err);
      setLoginError(err.response?.data?.error || "Invalid username or password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const homePage = getHomePageForRole(user?.role);

  const renderPage = () => {
    if (page === "dashboard") return <Dashboard user={user} />;
    if (page === "production") return <ProductionEntry user={user} setPage={navigate} />;
    if (page === "reports") return <Reports user={user} />;

    if (page === "profile") {
      return (
        <Profile
          user={user}
          onClose={() => navigate(homePage)}
          homePageLabel={
            homePage === "superadmin"
              ? "Super Admin Portal"
              : homePage === "manager"
              ? "Team Overview"
              : homePage === "admin"
              ? "Admin Portal"
              : "Dashboard"
          }
        />
      );
    }

    if (page === "reset") {
      return (
        <ResetPassword
          user={user}
          onClose={() => navigate(homePage)}
          homePageLabel={
            homePage === "superadmin"
              ? "Super Admin Portal"
              : homePage === "manager"
              ? "Team Overview"
              : homePage === "admin"
              ? "Admin Portal"
              : "Dashboard"
          }
        />
      );
    }

if (page === "superadmin") {
  if (user?.role !== "superadmin") {
    return <Dashboard user={user} />;
  }

  return (
    <SuperAdminPanel
      activeTab={superAdminTab}
      setActiveTab={setSuperAdminTab}
    />
  );
}

    if (page === "admin") {
      if (user?.role !== "admin") {
        return <Dashboard user={user} />;
      }
      return (
        <AdminPanel
          activeTab={adminTab}
          setActiveTab={setAdminTab}
        />
      );
    }

    if (page === "requests") return <Requests user={user} />;
    if (page === "inbox") return <Inbox user={user} />;
    if (page === "managerInbox") return <ManagerInbox user={user} />;
    if (page === "managerRequests") return <ManagerRequests user={user} />;
    if (page === "adminInbox") return <AdminInbox user={user} />;

    if (page === "manager") {
      return (
        <ManagerDashboard
          user={user}
          setPage={navigate}
          setSelectedEmployee={setSelectedEmployee}
        />
      );
    }

    if (page === "managerReports") return <ManagerReports user={user} />;

    if (page === "employeeDetails") {
      return (
        <EmployeeDetails
          userId={selectedEmployee}
          setPage={navigate}
        />
      );
    }

    return <Dashboard user={user} />;
  };

  
if (!user) {
    if (page === "landing") {
  return (
    <LandingPage onLoginClick={() => setPage("login")} />
  );
}

return (
      <div style={loginPage}>
        <style>{`
          @keyframes floatBlobA {
            0% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(20px, 18px) scale(1.05); }
            100% { transform: translate(0, 0) scale(1); }
          }

          @keyframes floatBlobB {
            0% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(-18px, -16px) scale(1.06); }
            100% { transform: translate(0, 0) scale(1); }
          }

          @keyframes cardEnter {
            0% {
              opacity: 0;
              transform: translateY(18px) scale(0.98);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes softPulse {
            0% { box-shadow: 0 10px 24px rgba(15,118,110,0.18); }
            50% { box-shadow: 0 14px 28px rgba(15,118,110,0.24); }
            100% { box-shadow: 0 10px 24px rgba(15,118,110,0.18); }
          }
        `}</style>

        <div style={bgGlowOne} />
        <div style={bgGlowTwo} />
        <div style={bgGlowThree} />

        <div style={loginCard}>
          <div style={loginTop}>
            <div style={logoCircle}>R</div>
            <h1 style={loginTitle}>RCM Tracker</h1>
            <p style={loginSubtitle}>Sign in to continue</p>
          </div>

          <div style={fieldWrap}>
            <div
              style={{
                ...inputWrap,
                ...(nameFocused ? inputWrapFocused : {})
              }}
            >
              <HiOutlineUser style={leftIcon} />

              {name.trim() === "" && (
                <span style={inputLabel}>Username</span>
              )}

              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (loginError) setLoginError("");
                }}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") login();
                }}
                style={loginInput}
                autoComplete="username"
              />
            </div>
          </div>

          <div style={fieldWrap}>
            <div
              style={{
                ...inputWrap,
                ...(passwordFocused ? inputWrapFocused : {})
              }}
            >
              <HiOutlineLockClosed style={leftIcon} />

              {password.trim() === "" && (
                <span style={inputLabel}>Password</span>
              )}

              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (loginError) setLoginError("");
                }}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") login();
                }}
                style={loginInputWithEye}
                autoComplete="current-password"
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseEnter={() => setEyeHover(true)}
                onMouseLeave={() => setEyeHover(false)}
                style={{
                  ...eyeBtn,
                  color: eyeHover ? theme.primary : theme.textMuted,
                  transform: eyeHover
                    ? "translateY(-50%) scale(1.06)"
                    : "translateY(-50%) scale(1)"
                }}
              >
                {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>

          {loginError && (
            <div style={errorBox}>
              <HiOutlineExclamationCircle style={{ fontSize: 18, flexShrink: 0 }} />
              <span>{loginError}</span>
            </div>
          )}

          <button
            onClick={login}
            disabled={loggingIn}
            onMouseEnter={() => setLoginBtnHover(true)}
            onMouseLeave={() => setLoginBtnHover(false)}
            style={{
              ...loginBtn,
              opacity: loggingIn ? 0.9 : 1,
              cursor: loggingIn ? "not-allowed" : "pointer",
              transform: loginBtnHover && !loggingIn ? "translateY(-2px)" : "translateY(0)",
              boxShadow:
                loginBtnHover && !loggingIn
                  ? "0 14px 28px rgba(15,118,110,0.24)"
                  : "0 10px 24px rgba(15,118,110,0.18)"
            }}
          >
            {loggingIn ? "Signing in..." : "Login"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", fontFamily: "sans-serif" }}>
      <style>{`
        @keyframes pageFadeIn {
          0% {
            opacity: 0;
            transform: translateY(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <Header
        user={user}
        setUser={setUser}
        setPage={navigate}
        setSidebarOpen={setSidebarOpen}
        onLogout={handleLogout}
      />

      {sidebarOpen && (
        <div
          style={overlay}
          onClick={() => setSidebarOpen(false)}
        />
      )}

<Sidebar
  setPage={navigate}
  user={user}
  sidebarOpen={sidebarOpen}
  setSidebarOpen={setSidebarOpen}
  setAdminTab={setAdminTab}
  setSuperAdminTab={setSuperAdminTab}
/>


      <div style={{ flex: 1 }}>
        <div
          style={{
            padding: `${HEADER_HEIGHT + 24}px 40px 30px 40px`,
            background: "#F8FAFC",
            minHeight: "100vh",
            animation: "pageFadeIn 0.35s ease"
          }}
        >
          {renderPage()}
        </div>
      </div>
    </div>
  );
}

const loginPage = {
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: "linear-gradient(135deg, #F0FDFA 0%, #F8FAFC 50%, #ECFEFF 100%)",
  padding: "24px",
  position: "relative",
  overflow: "hidden",
  fontFamily: "sans-serif"
};

const bgGlowOne = {
  position: "absolute",
  top: "-120px",
  left: "-100px",
  width: "320px",
  height: "320px",
  borderRadius: "50%",
  background: "rgba(20,184,166,0.12)",
  filter: "blur(30px)",
  animation: "floatBlobA 8s ease-in-out infinite"
};

const bgGlowTwo = {
  position: "absolute",
  right: "-100px",
  bottom: "-120px",
  width: "320px",
  height: "320px",
  borderRadius: "50%",
  background: "rgba(15,118,110,0.10)",
  filter: "blur(30px)",
  animation: "floatBlobB 9s ease-in-out infinite"
};

const bgGlowThree = {
  position: "absolute",
  top: "18%",
  right: "18%",
  width: "180px",
  height: "180px",
  borderRadius: "50%",
  background: "rgba(45,212,191,0.10)",
  filter: "blur(24px)",
  animation: "floatBlobA 10s ease-in-out infinite"
};

const loginCard = {
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: "360px",
  background: "rgba(255,255,255,0.95)",
  borderRadius: "22px",
  padding: "28px",
  border: "1px solid #E6FFFB",
  boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
  backdropFilter: "blur(8px)",
  animation: "cardEnter 0.45s ease"
};

const loginTop = {
  textAlign: "center",
  marginBottom: "22px"
};

const logoCircle = {
  width: "54px",
  height: "54px",
  margin: "0 auto 12px",
  borderRadius: "16px",
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  fontWeight: 800,
  boxShadow: "0 10px 24px rgba(15,118,110,0.18)",
  animation: "softPulse 3s ease-in-out infinite"
};

const loginTitle = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 700,
  color: theme.text
};

const loginSubtitle = {
  margin: "8px 0 0 0",
  fontSize: "13px",
  color: theme.textMuted
};

const fieldWrap = {
  marginBottom: "16px"
};

const inputWrap = {
  position: "relative",
  width: "100%",
  height: "48px",
  border: `1px solid ${theme.border}`,
  borderRadius: "14px",
  background: "#fff",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  overflow: "hidden"
};

const inputWrapFocused = {
  borderColor: theme.borderFocus,
  boxShadow: "0 0 0 3px rgba(20,184,166,0.10)"
};

const leftIcon = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.primary,
  fontSize: "18px",
  pointerEvents: "none",
  zIndex: 2
};

const inputLabel = {
  position: "absolute",
  left: "42px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "13px",
  pointerEvents: "none",
  zIndex: 1
};

const loginInput = {
  width: "100%",
  height: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  padding: "0 14px 0 42px",
  fontSize: "14px",
  color: theme.text,
  boxSizing: "border-box",
  position: "relative",
  zIndex: 2
};

const loginInputWithEye = {
  width: "100%",
  height: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  padding: "0 42px 0 42px",
  fontSize: "14px",
  color: theme.text,
  boxSizing: "border-box",
  position: "relative",
  zIndex: 2
};

const eyeBtn = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  zIndex: 3,
  transition: "all 0.18s ease"
};

const errorBox = {
  marginBottom: "14px",
  padding: "10px 12px",
  borderRadius: "12px",
  background: theme.dangerBg,
  color: theme.danger,
  border: `1px solid ${theme.dangerBorder}`,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: 1.4
};

const loginBtn = {
  width: "100%",
  height: "46px",
  borderRadius: "14px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#fff",
  fontWeight: 700,
  fontSize: "15px",
  transition: "all 0.2s ease"
};

const overlay = {
  position: "fixed",
  top: `${HEADER_HEIGHT}px`,
  left: 0,
  width: "100%",
  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
  background: "rgba(0,0,0,0.3)",
  zIndex: 998
};

export default App;
